import React, { useState, useRef, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { ReactComponent as ChevronDownIcon } from '../../../assets/images/chevron-down.svg';
import { uploadImages } from '../../../services/postApi';
import { apiClient } from '../../../services';
import { getLectureQnaPosts, QnaPost, getPostDetail, PostDetail } from '../../../services/lectureApi';
import { searchLectureQnA, PostSearchItem } from '../../../services/searchApi';
import RichTextEditor from '../../../components/common/RichTextEditor';
import type { RichTextEditorHandle } from '../../../components/common/RichTextEditor';

interface Question {
  id: number;
  title: string;
  content: string;
  author: string;
  date: string;
  answers: number;
  views: number;
  images?: string[]; // 첨부된 이미지들
  chapter?: number; // 챕터 정보 추가
}

interface StreamingQnAProps {
  lectureId?: string;
  lectureName?: string; // 강의 이름 추가
}

const StreamingQnA: React.FC<StreamingQnAProps> = ({ lectureId, lectureName }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [isClosing, setIsClosing] = useState(false); // 닫기 애니메이션 상태 추가
  const [questionTitle, setQuestionTitle] = useState('');
  const [questionContent, setQuestionContent] = useState('');
  const [hasMoreContent, setHasMoreContent] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false); // 질문 제출 중 상태 추가
  
  // 실제 API 데이터 관련 상태
  const [qnaPosts, setQnaPosts] = useState<QnaPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // 검색 관련 상태
  const [searchResults, setSearchResults] = useState<PostSearchItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isSearchMode, setIsSearchMode] = useState(false);
  
  // 상세 페이지 관련 상태 추가
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [postDetail, setPostDetail] = useState<PostDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  
  const questionsListRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<RichTextEditorHandle>(null);

  // 강의별 Q&A 데이터 로드
  useEffect(() => {
    const fetchQnAData = async () => {
      if (!lectureId) {
        setError('강의 ID가 없습니다.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        console.log('Q&A 데이터 로드 시작:', lectureId);
        
        const response = await getLectureQnaPosts(parseInt(lectureId), 0, 20); // 첫 페이지, 20개씩
        
        if (response.code === 200 || response.code === 201) {
          setQnaPosts(response.data.content);
          
          console.log('Q&A 데이터 로드 성공:', response.data);
        } else {
          setError(response.message || 'Q&A 목록을 불러오는데 실패했습니다.');
        }
      } catch (err) {
        console.error('Q&A 데이터 로드 실패:', err);
        setError('Q&A 목록을 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchQnAData();
  }, [lectureId]);

  // PostSearchItem을 Question 형태로 변환
  const convertSearchItemToQuestion = (item: PostSearchItem): Question => ({
    id: item.id,
    title: item.title,
    content: item.htmlcontent,
    author: item.writerName,
    date: new Date(item.postDate).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }),
    answers: 0, // 검색 결과에서는 답변 수 제공안됨
    views: 0, // 검색 결과에서는 조회수 제공안됨
  });

  // API 데이터를 Question 형태로 변환
  const apiQuestions: Question[] = useMemo(() => {
    return qnaPosts.map(post => ({
      id: post.post_id,
      title: post.title,
      content: post.content,
      author: post.writer_name,
      date: new Date(post.created_at).toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }),
      answers: post.reply_cnt,
      views: post.view_cnt,
      chapter: post.chapter
    }));
  }, [qnaPosts]);

  // 검색 결과를 Question 형태로 변환
  const searchQuestions: Question[] = useMemo(() => {
    return searchResults.map(convertSearchItemToQuestion);
  }, [searchResults]);

  const questions: Question[] = useMemo(() => {
    // 검색 모드일 때는 검색 결과, 아니띢 때는 전체 목록
    return isSearchMode ? searchQuestions : apiQuestions;
  }, [isSearchMode, searchQuestions, apiQuestions]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // 검색 수행 함수
  const performSearch = async (keyword: string) => {
    if (!keyword.trim()) {
      // 검색어가 비어있으면 일반 모드로 돌아가기
      setIsSearchMode(false);
      setSearchResults([]);
      setSearchError(null);
      return;
    }

    if (!lectureId) {
      setSearchError('강의 ID가 없습니다.');
      return;
    }

    try {
      setIsSearching(true);
      setSearchError(null);
      setIsSearchMode(true);
      
      console.log('Q&A 검색 시작:', { lectureId, keyword });
      
      const results = await searchLectureQnA(parseInt(lectureId), keyword);
      
      console.log('Q&A 검색 결과:', results);
      setSearchResults(results);
      
    } catch (error) {
      console.error('Q&A 검색 실패:', error);
      setSearchError('검색 중 오류가 발생했습니다.');
    } finally {
      setIsSearching(false);
    }
  };

  // 검색어 디바운스 처리
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      performSearch(searchQuery);
    }, 300); // 300ms 디바운스

    return () => clearTimeout(timeoutId);
  }, [searchQuery, lectureId, performSearch]);

  const handleQuestionSubmit = async () => {
    if (!questionTitle.trim() || !questionContent.trim()) {
      alert('제목과 내용을 모두 입력해주세요.');
      return;
    }

    if (!lectureId) {
      alert('강의 정보를 찾을 수 없습니다.');
      return;
    }

    try {
      setIsSubmitting(true);
      
      // 에디터에서 이미지 삽입을 처리하므로 별도 업로드/주입 없음
      const htmlContent = renderContent(questionContent);

      // 3. 게시글 등록
      const postData = {
        title: questionTitle,
        htmlContent: htmlContent,
        category: '질문',
        lectureId: parseInt(lectureId),
        exposed: true
      };

      console.log('게시글 등록 데이터:', postData);

      const { data: responseData } = await apiClient.post<{
        code: number; status: string; message: string; data: any
      }>(
        '/api/posts',
        postData,
        { withCredentials: true }
      );
      if (responseData.code !== 200 && responseData.code !== 201) {
        throw new Error(responseData.message || '질문 등록에 실패했습니다.');
      }
      console.log('질문 등록 성공:', responseData);

      // 4. 성공 처리
      alert('질문이 성공적으로 등록되었습니다!');
      
      // 폼 초기화
      setQuestionTitle('');
      setQuestionContent('');
      setShowQuestionForm(false);
      
      // 검색 상태 초기화 (새 질문이 추가되었으므로 일반 목록으로 돌아가기)
      setIsSearchMode(false);
      setSearchResults([]);
      setSearchError(null);
      setSearchQuery('');
      
      // 질문 목록 새로고침
      if (lectureId) {
        const response = await getLectureQnaPosts(parseInt(lectureId), 0, 20);
        if (response.code === 200 || response.code === 201) {
          setQnaPosts(response.data.content);
        }
      }

    } catch (error) {
      console.error('질문 등록 실패:', error);
      alert(`질문 등록에 실패했습니다: ${error instanceof Error ? error.message : '알 수 없는 오류'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    try {
      const uploadRes = await uploadImages(files);
      const urls: string[] = uploadRes.data || [];
      let htmlContent = renderContent(questionContent);
      urls.forEach((u) => {
        htmlContent += `<img src="${u}" alt="첨부 이미지" style="max-width: 50%; height: auto; margin: 10px 0; display: block;" />`;
      });
      setQuestionContent(htmlContent);
    } catch (err) {
      console.error('이미지 업로드 실패', err);
      alert('이미지 업로드에 실패했습니다.');
    } finally {
      e.target.value = '';
    }
  };

  // 마크다운 코드 블록을 HTML로 변환하는 함수
  const renderContent = (content: string) => {
    // ```로 감싸진 코드 블록을 <pre><code>로 변환
    const codeBlockRegex = /```[\w]*\n?([\s\S]*?)```/g;
    const htmlContent = content.replace(codeBlockRegex, (match, code) => {
      return `<pre><code>${code.trim()}</code></pre>`;
    });
    
    // 인라인 코드 `code`를 <code>로 변환
    const inlineCodeRegex = /`([^`]+)`/g;
    const finalContent = htmlContent.replace(inlineCodeRegex, '<code>$1</code>');
    
    return finalContent;
  };

  const toggleQuestionForm = () => {
    if (showQuestionForm) {
      // 닫기 애니메이션 시작
      setIsClosing(true);
      setTimeout(() => {
        setShowQuestionForm(false);
        setIsClosing(false);
      }, 300); // 애니메이션 시간과 동일
    } else {
      // 열기
      setShowQuestionForm(true);
      setIsClosing(false);
    }
  };

  const handleQuestionClick = async (question: Question) => {
    try {
      setDetailLoading(true);
      setDetailError(null);
      setSelectedQuestion(question);
      setShowDetail(true);
      
      console.log('게시글 상세조회 시작:', question.id);
      
      const response = await getPostDetail(question.id);
      
      if (response.code === 200 || response.code === 201) {
        setPostDetail(response.data);
        console.log('게시글 상세조회 성공:', response.data);
      } else {
        setDetailError(response.message || '게시글을 불러오는데 실패했습니다.');
      }
    } catch (error) {
      console.error('게시글 상세조회 실패:', error);
      setDetailError('게시글을 불러오는데 실패했습니다.');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleBackClick = () => {
    setShowDetail(false);
    setSelectedQuestion(null);
    setPostDetail(null);
    setDetailError(null);
  };

  const handleLectureQuestionRedirect = () => {
    // 전체화면 새 창으로 커뮤니티 강의 질문 페이지 열기
    const screenWidth = window.screen.availWidth;
    const screenHeight = window.screen.availHeight;
    
    const newWindow = window.open(
      '/community/lecture-questions', 
      '_blank', 
      `width=${screenWidth},height=${screenHeight},left=0,top=0,scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=no,status=no`
    );
    
    if (newWindow) {
      console.log('전체화면 새 창에서 커뮤니티 페이지 열기 성공');
      newWindow.focus();
    } else {
      console.warn('팝업 차단으로 인해 새 창을 열 수 없습니다');
      alert('팝업 차단을 해제하고 다시 시도해주세요.');
    }
  };

  // 페이지 제목 설정
  useEffect(() => {
    const originalTitle = document.title;
    
    if (lectureName) {
      document.title = `투비닥터 캠퍼스 - ${lectureName}`;
    } else {
      document.title = '투비닥터 캠퍼스 - 젊은 의사의 학습 러닝메이트';
    }
    
    // 컴포넌트 언마운트 시 원래 제목으로 복원
    return () => {
      document.title = originalTitle;
    };
  }, [lectureName]);

  // 스크롤 감지하여 하단 그라데이션 표시 여부 결정
  useEffect(() => {
    const handleScroll = () => {
      const scrollElement = showDetail ? 
        questionsListRef.current?.querySelector('[data-detail-container]') : 
        questionsListRef.current;
        
      if (scrollElement) {
        const { scrollTop, scrollHeight, clientHeight } = scrollElement;
        const isNearBottom = scrollTop + clientHeight >= scrollHeight - 10;
        setHasMoreContent(!isNearBottom);
      }
    };

    const questionsListElement = questionsListRef.current;
    if (questionsListElement) {
      if (showDetail) {
        const detailContainer = questionsListElement.querySelector('[data-detail-container]');
        if (detailContainer) {
          detailContainer.addEventListener('scroll', handleScroll);
          handleScroll(); // 초기 체크
          
          return () => {
            detailContainer.removeEventListener('scroll', handleScroll);
          };
        }
      } else {
        questionsListElement.addEventListener('scroll', handleScroll);
        handleScroll(); // 초기 체크
        
        return () => {
          questionsListElement.removeEventListener('scroll', handleScroll);
        };
      }
    }
  }, [questions, showDetail]);

  // 로딩 상태 표시 (초기 데이터 로딩시에만)
  if (loading && !isSearchMode) {
    return (
      <QnAContainer>
        <LoadingContainer>
          <LoadingText>Q&A 목록을 불러오는 중...</LoadingText>
        </LoadingContainer>
      </QnAContainer>
    );
  }

  // 에러 상태 표시 (초기 데이터 로딩 실패시에만)
  if (error && !isSearchMode) {
    return (
      <QnAContainer>
        <ErrorContainer>
          <ErrorText>{error}</ErrorText>
          <RetryButton onClick={() => window.location.reload()}>
            다시 시도
          </RetryButton>
        </ErrorContainer>
      </QnAContainer>
    );
  }

  return (
    <QnAContainer>
      <SearchSection>
        <SearchInputWrapper>
          {isSearching ? (
            <SearchIcon>
              <LoadingSpinner />
            </SearchIcon>
          ) : (
            <SearchIcon>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M8.33331 1.75C4.69745 1.75 1.75 4.69746 1.75 8.33333C1.75 11.9692 4.69745 14.9167 8.33331 14.9167C9.88105 14.9167 11.3041 14.3826 12.428 13.4886L16.5531 17.6136C16.846 17.9065 17.3208 17.9065 17.6137 17.6136C17.9066 17.3207 17.9066 16.8459 17.6137 16.553L13.4887 12.4279C14.3826 11.3039 14.9166 9.88101 14.9166 8.33333C14.9166 4.69746 11.9692 1.75 8.33331 1.75ZM3.24999 8.33333C3.24999 5.52589 5.52587 3.25 8.33331 3.25C11.1407 3.25 13.4166 5.52589 13.4166 8.33333C13.4166 11.1408 11.1407 13.4167 8.33331 13.4167C5.52587 13.4167 3.24999 11.1408 3.24999 8.33333Z" fill="#37383C" fillOpacity="0.28"/>
              </svg>
            </SearchIcon>
          )}
          <SearchInput
            type="text"
            placeholder="검색어를 입력해주세요."
            value={searchQuery}
            onChange={handleSearch}
          />
        </SearchInputWrapper>
        
        {/* 상세 페이지일 때는 뒤로가기, 아닐 때는 강의 질문 바로가기 */}
        {showDetail ? (
          <BackButton onClick={handleBackClick}>
            <BackIcon>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </BackIcon>
            <BackText>뒤로가기</BackText>
          </BackButton>
        ) : (
          <QuickAccessWrapper onClick={handleLectureQuestionRedirect}>
            <QuickAccessText>강의 질문 바로가기</QuickAccessText>
            <ChevronRightIcon>
              <ChevronDownIcon />
            </ChevronRightIcon>
          </QuickAccessWrapper>
        )}
      </SearchSection>

      <ContentSection $showDetail={showDetail}>
        <QuestionsList ref={questionsListRef} $showDetail={showDetail}>
          {!showDetail ? (
            /* 검색 오류 표시 */
            searchError ? (
              <ErrorContainer>
                <ErrorText>{searchError}</ErrorText>
                <RetryButton onClick={() => performSearch(searchQuery)}>
                  다시 시도
                </RetryButton>
              </ErrorContainer>
            ) : questions.length > 0 ? (
              questions.map((question, index) => (
              <QuestionItem key={question.id} onClick={() => handleQuestionClick(question)}>
                <QuestionContent>
                  <QuestionHeader>
                    <QuestionTitle>{question.title}</QuestionTitle>
                    <QuestionBody>
                      <QuestionContentDisplay 
                        dangerouslySetInnerHTML={{ __html: renderContent(question.content) }}
                      />
                      {/* 첨부된 이미지들 표시 */}
                      {question.images && question.images.length > 0 && (
                        <AttachedImages>
                          {question.images.map((imageUrl, imgIndex) => (
                            <AttachedImage key={imgIndex} src={imageUrl} alt={`첨부 이미지 ${imgIndex + 1}`} />
                          ))}
                        </AttachedImages>
                      )}
                    </QuestionBody>
                </QuestionHeader>
                
                <QuestionMeta>
                  <LeftMeta>
                    <AuthorAvatar />
                    <AuthorName>{question.author}</AuthorName>
                    <Separator />
                    <QuestionDate>{question.date}</QuestionDate>
                  </LeftMeta>
                  <RightMeta>
                    <AnswerCount>답변 {question.answers}</AnswerCount>
                    <Separator />
                    <ViewCount>조회수 {question.views}</ViewCount>
                  </RightMeta>
                </QuestionMeta>
              </QuestionContent>
              {/* 마지막 아이템이 아닐 때만 구분선 표시 */}
              {index < questions.length - 1 && <QuestionDivider />}
            </QuestionItem>
          ))
            ) : isSearchMode ? (
              /* 검색 결과가 없을 때 */
              <EmptyQnAContainer>
                <EmptyQnAText>'{searchQuery}' 에 대한 검색 결과가 없습니다.</EmptyQnAText>
                <EmptyQnASubText>다른 검색어로 다시 시도해보세요.</EmptyQnASubText>
              </EmptyQnAContainer>
            ) : (
              /* 일반 모드에서 질문이 없을 때 */
              <EmptyQnAContainer>
                <EmptyQnAText>아직 등록된 질문이 없습니다.</EmptyQnAText>
                <EmptyQnASubText>첫 번째 질문을 남겨보세요!</EmptyQnASubText>
              </EmptyQnAContainer>
            )
          ) : (
            /* 상세 페이지 */
            selectedQuestion && (
              <QuestionDetailContainer data-detail-container>
                {detailLoading ? (
                  /* 상세 정보 로딩 중 */
                  <DetailLoadingContainer>
                    <DetailLoadingText>게시글을 불러오는 중...</DetailLoadingText>
                  </DetailLoadingContainer>
                ) : detailError ? (
                  /* 상세 정보 로드 실패 */
                  <DetailErrorContainer>
                    <DetailErrorText>{detailError}</DetailErrorText>
                    <DetailRetryButton onClick={() => handleQuestionClick(selectedQuestion)}>
                      다시 시도
                    </DetailRetryButton>
                  </DetailErrorContainer>
                ) : postDetail ? (
                  /* 실제 상세 정보 표시 */
                  <QuestionDetailContent>
                      <QuestionDetailHeader>
                        <QuestionDetailTitle>{postDetail.post_title}</QuestionDetailTitle>
                        <QuestionDetailMeta>
                          <LeftMeta>
                            <AuthorAvatar />
                            <AuthorName>{postDetail.writer_name}</AuthorName>
                            <Separator />
                            <QuestionDate>
                              {new Date(postDetail.created_at).toLocaleDateString('ko-KR', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: false
                              })}
                            </QuestionDate>
                            {postDetail.lecture_chapter && (
                              <>
                                <Separator />
                                <ChapterTag>챕터 {postDetail.lecture_chapter}</ChapterTag>
                              </>
                            )}
                          </LeftMeta>
                          <RightMeta>
                            <AnswerCount>답변 {postDetail.reply_cnt}</AnswerCount>
                            <Separator />
                            <ViewCount>조회수 {postDetail.view_cnt}</ViewCount>
                          </RightMeta>
                        </QuestionDetailMeta>
                        <DetailDivider />
                      </QuestionDetailHeader>

                      <QuestionDetailBody>
                        <QuestionDetailText 
                          dangerouslySetInnerHTML={{ __html: renderContent(postDetail.post_content) }}
                        />
                    
                    {/* 임시 이미지 추가 */}
                    {/* <AttachedImages>
                      <AttachedImage src="/temp-question-image.png" alt="게시글 이미지" />
                    </AttachedImages> */}
                    
                    {selectedQuestion.images && selectedQuestion.images.length > 0 && (
                      <AttachedImages>
                        {selectedQuestion.images.map((imageUrl, imgIndex) => (
                          <AttachedImage key={imgIndex} src={imageUrl} alt={`첨부 이미지 ${imgIndex + 1}`} />
                        ))}
                      </AttachedImages>
                    )}
                    
                    {/* 코드블록 예시 */}
                    {/* <CodeBlock>
                      코드블록 입력 예시입니다. 이름이 코드블록이긴 한데 여기에만 쓰겠다고 새로운 폰트 스타일과 색상을 정의하고 하기도 귀찮고.. 그냥 아무 글자나 이런식으로 입력할 수 있지만 박스로 본문이랑 구분만 하도록 해두면 될 것 같습니다. (레퍼런스: 스파르타코딩클럽 게시판)
                      <br />
                      일반 본문과 글자 색이 한 단계 다릅니다. (semantic/label/neutral)
                    </CodeBlock> */}

                    {/* 첨부파일 예시 */}
                    {/* <AttachmentItem>
                      <AttachmentIcon>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10.3504 3.89961C9.02491 3.89961 7.95039 4.97413 7.95039 6.29961V16.0496C7.95039 18.2864 9.76364 20.0996 12.0004 20.0996C14.2371 20.0996 16.0504 18.2864 16.0504 16.0496V5.92984C16.0504 5.43279 16.4533 5.02984 16.9504 5.02984C17.4474 5.02984 17.8504 5.43279 17.8504 5.92984V16.0496C17.8504 19.2805 15.2313 21.8996 12.0004 21.8996C8.76953 21.8996 6.15039 19.2805 6.15039 16.0496V6.29961C6.15039 3.98001 8.0308 2.09961 10.3504 2.09961C12.67 2.09961 14.5504 3.98001 14.5504 6.29961V16.0008C14.5504 17.4091 13.4087 18.5508 12.0004 18.5508C10.5921 18.5508 9.45039 17.4091 9.45039 16.0008V7.18566C9.45039 6.6886 9.85333 6.28566 10.3504 6.28566C10.8474 6.28566 11.2504 6.6886 11.2504 7.18566V16.0008C11.2504 16.415 11.5862 16.7508 12.0004 16.7508C12.4146 16.7508 12.7504 16.415 12.7504 16.0008V6.29961C12.7504 4.97413 11.6759 3.89961 10.3504 3.89961Z" fill="currentColor"/>
                        </svg>
                      </AttachmentIcon>
                      <AttachmentInfo>
                        <AttachmentName>파일명.확장자</AttachmentName>
                      </AttachmentInfo>
                      <AttachmentRemove>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9.63589 8.3636C9.28441 8.01213 8.71457 8.01213 8.3631 8.3636C8.01163 8.71508 8.01163 9.28492 8.3631 9.6364L10.7267 12L8.3631 14.3636C8.01163 14.7151 8.01163 15.2849 8.3631 15.6364C8.71457 15.9879 9.28441 15.9879 9.63589 15.6364L11.9995 13.2728L14.3631 15.6364C14.7145 15.9879 15.2844 15.9879 15.6359 15.6364C15.9873 15.2849 15.9873 14.7151 15.6359 14.3636L13.2723 12L15.6359 9.6364C15.9873 9.28492 15.9873 8.71508 15.6359 8.3636C15.2844 8.01213 14.7145 8.01213 14.3631 8.3636L11.9995 10.7272L9.63589 8.3636Z" fill="currentColor"/>
                          <path fillRule="evenodd" clipRule="evenodd" d="M11.9995 2.09961C6.53188 2.09961 2.09952 6.53199 2.09952 11.9996C2.09952 17.4672 6.53188 21.8996 11.9995 21.8996C17.4671 21.8996 21.8994 17.4672 21.8994 11.9996C21.8994 6.53199 17.4671 2.09961 11.9995 2.09961ZM3.89951 11.9996C3.89951 7.5261 7.52599 3.89961 11.9995 3.89961C16.473 3.89961 20.0994 7.5261 20.0994 11.9996C20.0994 16.4731 16.473 20.0996 11.9995 20.0996C7.52599 20.0996 3.89951 16.4731 3.89951 11.9996Z" fill="currentColor"/>
                        </svg>
                      </AttachmentRemove>
                    </AttachmentItem> */}

                    <DetailDivider />
                  </QuestionDetailBody>

                  {/* 답변 섹션 */}
                  <AnswerSection>
                    <AnswerHeader>
                      <AnswerIcon>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12.0004 2.35046C6.67084 2.35046 2.3504 6.67092 2.3504 12.0005C2.3504 17.33 6.67084 21.6505 12.0004 21.6505C13.5387 21.6505 14.9951 21.2899 16.2875 20.648L18.0999 21.1423C18.5101 21.2542 18.876 21.354 19.1766 21.4071C19.4811 21.4609 19.8672 21.4975 20.2529 21.3503C20.7581 21.1573 21.1572 20.7582 21.3501 20.253C21.4974 19.8673 21.4607 19.4813 21.407 19.1767C21.3539 18.8761 21.2541 18.5103 21.1422 18.1001L20.6479 16.2876C21.2897 14.9952 21.6503 13.5388 21.6503 12.0005C21.6503 6.67092 17.3299 2.35046 12.0004 2.35046Z" fill="currentColor"/>
                        </svg>
                      </AnswerIcon>
                      <AnswerTitle>답변 {postDetail ? postDetail.reply_cnt : selectedQuestion.answers}</AnswerTitle>
                    </AnswerHeader>

                    <AnswerList>
                      {postDetail && postDetail.replies.length > 0 ? (
                        postDetail.replies.map((reply) => (
                          <AnswerItem key={reply.reply_id}>
                            <AnswerAuthor>
                              <AuthorAvatar />
                              <AnswerAuthorInfo>
                                <AnswerAuthorName>{reply.writer_name}</AnswerAuthorName>
                                <AnswerDate>
                                  {new Date(reply.reply_created_at).toLocaleDateString('ko-KR', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: false
                                  })}
                                </AnswerDate>
                              </AnswerAuthorInfo>
                            </AnswerAuthor>
                            <AnswerContent 
                              dangerouslySetInnerHTML={{ __html: renderContent(reply.content) }}
                            />
                            {/* 답변에 첨부된 이미지들 표시 */}
                            {reply.images && reply.images.length > 0 && (
                              <ReplyImages>
                                {reply.images.map((imageUrl, imageIndex) => (
                                  <ReplyImage key={imageIndex} src={imageUrl} alt={`답변 첨부 이미지 ${imageIndex + 1}`} />
                                ))}
                              </ReplyImages>
                            )}
                            <DetailDivider />
                          </AnswerItem>
                        ))
                      ) : (
                        <NoAnswerContainer>
                          <NoAnswerText>아직 답변이 없습니다.</NoAnswerText>
                        </NoAnswerContainer>
                      )}
                    </AnswerList>
                  </AnswerSection>
                </QuestionDetailContent>
                ) : null}
              </QuestionDetailContainer>
            )
          )}
        </QuestionsList>

        {/* 하단 그라데이션 - 스크롤 감지에 따라 표시 */}
        {hasMoreContent && <BottomGradient $showDetail={showDetail} />}
        
        {showQuestionForm && (
          <QuestionFormSection $isClosing={isClosing}>
            <FormContainer>
              <TitleInputContainer>
                <TitleInput
                  type="text"
                  placeholder="제목을 입력해주세요."
                  value={questionTitle}
                  onChange={(e) => setQuestionTitle(e.target.value)}
                />
              </TitleInputContainer>
              
              {/* 질문 내용 입력 영역 - PostDetail과 동일한 구조 */}
              <div style={{
                alignSelf: 'stretch',
                border: '1px solid var(--Line-Normal-Neutral)',
                borderRadius: '12px',
                overflow: 'hidden'
              }}>
                {/* 텍스트 입력 영역 (RichTextEditor) */}
                <div style={{ padding: '0', minHeight: '200px' }}>
                  <RichTextEditor
                    ref={editorRef as React.RefObject<RichTextEditorHandle>}
                    value={questionContent}
                    onChange={setQuestionContent}
                    placeholder="질문 내용을 입력해주세요."
                    minHeight="200px"
                    attachmentsEditable={true}
                    hideToolbar={true}
                  />
                </div>
                
                {/* 하단 툴바 */}
                <div style={{
                  padding: '16px 24px',
                  background: 'var(--Fill-Normal)',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  display: 'flex'
                }}>
                  {/* 툴바 버튼들 */}
                  <div style={{
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    gap: '16px',
                    display: 'flex'
                  }}>
                    {/* 이미지 버튼: 에디터 내부 파일 입력 호출 */}
                    <ImageButton onClick={() => fileInputRef.current?.click()} disabled={isSubmitting}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M6.34947 8.74903C6.34947 7.42354 7.42398 6.34903 8.74946 6.34903C10.0749 6.34903 11.1495 7.42354 11.1495 8.74903C11.1495 10.0745 10.0749 11.149 8.74946 11.149C7.42398 11.149 6.34947 10.0745 6.34947 8.74903ZM8.74946 8.14903C8.41809 8.14903 8.14946 8.41766 8.14946 8.74903C8.14946 9.0804 8.41809 9.34903 8.74946 9.34903C9.08083 9.34903 9.34946 9.0804 9.34946 8.74903C9.34946 8.41766 9.08083 8.14903 8.74946 8.14903Z"
                          fill="currentColor"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M8.26189 2.59961C7.45387 2.5996 6.79298 2.59959 6.25591 2.64347C5.7 2.68889 5.19793 2.78578 4.72904 3.02469C3.99521 3.39859 3.39859 3.99522 3.02468 4.72905C2.78577 5.19794 2.68889 5.70002 2.64347 6.25593C2.59959 6.793 2.5996 7.45385 2.59961 8.26189V15.7373C2.5996 16.5454 2.59959 17.2062 2.64347 17.7433C2.68889 18.2992 2.78577 18.8013 3.02468 19.2702C3.39859 20.004 3.99521 20.6006 4.72904 20.9745C5.19793 21.2135 5.7 21.3103 6.25591 21.3558C6.79299 21.3996 7.45384 21.3996 8.26189 21.3996H15.7373C16.5453 21.3996 17.2062 21.3996 17.7432 21.3558C18.2991 21.3103 18.8012 21.2135 19.2701 20.9745C20.0039 20.6006 20.6006 20.004 20.9745 19.2702C21.2134 18.8013 21.3103 18.2992 21.3557 17.7433C21.3996 17.2062 21.3996 16.5454 21.3995 15.7373V8.26191C21.3996 7.45386 21.3996 6.79301 21.3557 6.25593C21.3103 5.70002 21.2134 5.19794 20.9745 4.72905C20.6006 3.99522 20.0039 3.39859 19.2701 3.02469C18.8012 2.78578 18.2991 2.68889 17.7432 2.64347C17.2062 2.59959 16.5454 2.5996 15.7373 2.59961H8.26189ZM5.54622 4.6285C5.71907 4.54043 5.95789 4.47382 6.40249 4.4375C6.8576 4.40031 7.44466 4.39961 8.29959 4.39961H15.6996C16.5545 4.39961 17.1416 4.40031 17.5967 4.4375C18.0413 4.47382 18.2801 4.54043 18.4529 4.6285C18.8481 4.82983 19.1693 5.15109 19.3707 5.54623C19.4587 5.71908 19.5253 5.95791 19.5617 6.40251C19.5989 6.85761 19.5996 7.44468 19.5996 8.29961V10.8275L19.3735 10.6014C18.9989 10.2267 18.6798 9.90768 18.3965 9.66715C18.0988 9.41446 17.7819 9.19599 17.3956 9.07046C16.8131 8.88122 16.1857 8.88122 15.6033 9.07046C15.2169 9.19599 14.9 9.41446 14.6023 9.66715C14.319 9.90767 14 10.2267 13.6254 10.6014L5.13281 19.0939C4.92563 18.9163 4.75382 18.699 4.62849 18.453C4.54042 18.2802 4.47381 18.0413 4.43749 17.5967C4.4003 17.1416 4.3996 16.5545 4.3996 15.6996V8.29961C4.3996 7.44468 4.4003 6.85761 4.43749 6.40251C4.47381 5.95791 4.54042 5.71908 4.62849 5.54623C4.82982 5.15109 5.15108 4.82983 5.54622 4.6285ZM7.17809 19.5943C7.49378 19.5994 7.86152 19.5996 8.29959 19.5996H15.6996C16.5545 19.5996 17.1416 19.5989 17.5967 19.5617C18.0413 19.5254 18.2801 19.4588 18.4529 19.3707C18.8481 19.1694 19.1693 18.8481 19.3707 18.453C19.4587 18.2802 19.5253 18.0413 19.5617 17.5967C19.5989 17.1416 19.5996 16.5545 19.5996 15.6996V13.373L18.1257 11.8992C17.7192 11.4927 17.4521 11.2266 17.2316 11.0394C17.019 10.8589 16.9097 10.8052 16.8393 10.7824C16.6184 10.7106 16.3804 10.7106 16.1595 10.7824C16.0892 10.8052 15.9798 10.8589 15.7672 11.0394C15.5467 11.2266 15.2796 11.4927 14.8731 11.8992L7.17809 19.5943Z"
                          fill="currentColor"
                        />
                      </svg>
                    </ImageButton>
                    
                    {/* 코드 버튼: 선택 유지 후 코드블록 토글 */}
                    <CodeButton onClick={() => {
                      // 에디터의 선택 상태를 그대로 두고 코드 토글을 호출
                      const inst: any = editorRef?.current;
                      if (inst && typeof inst.toggleCodeBlock === 'function') {
                        inst.toggleCodeBlock();
                      }
                    }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M14.2187 3.12669C14.7009 3.24725 14.9941 3.73589 14.8736 4.2181L10.8736 20.2181C10.753 20.7003 10.2644 20.9935 9.78217 20.873C9.29996 20.7524 9.00678 20.2638 9.12733 19.7815L13.1273 3.78154C13.2479 3.29932 13.7365 3.00614 14.2187 3.12669Z"
                          fill="currentColor"
                        />
                        <path
                          d="M16.364 6.36343C16.0126 6.7149 16.0126 7.28475 16.364 7.63622L20.7276 11.9998L16.364 16.3634C16.0126 16.7149 16.0126 17.2847 16.364 17.6362C16.7155 17.9877 17.2854 17.9877 17.6368 17.6362L22.6368 12.6362C22.9883 12.2847 22.9883 11.7149 22.6368 11.3634L17.6368 6.36343C17.2854 6.01195 16.7155 6.01195 16.364 6.36343Z"
                          fill="currentColor"
                        />
                        <path
                          d="M7.63696 7.63622C7.98843 7.28475 7.98843 6.7149 7.63696 6.36343C7.28549 6.01195 6.71564 6.01195 6.36417 6.36343L1.36419 11.3634C1.01272 11.7149 1.01272 12.2847 1.36419 12.6362L6.36417 17.6362C6.71564 17.9877 7.28549 17.9877 7.63696 17.6362C7.98843 17.2847 7.98843 16.7149 7.63696 16.3634L3.27337 11.9998L7.63696 7.63622Z"
                          fill="currentColor"
                        />
                      </svg>
                    </CodeButton>
                  </div>
                  
                  {/* 등록하기 버튼 */}
                  <button
                    onClick={handleQuestionSubmit}
                    disabled={!questionTitle.trim() || !questionContent.trim() || isSubmitting}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      cursor: (!questionTitle.trim() || !questionContent.trim() || isSubmitting) ? 'not-allowed' : 'pointer',
                      color: (!questionTitle.trim() || !questionContent.trim() || isSubmitting) ? 'var(--Label-Alternative)' : 'var(--Label-Normal)',
                      fontSize: '16px',
                      fontFamily: 'Pretendard JP',
                      fontWeight: '600',
                      lineHeight: '24px',
                      letterSpacing: '0.09px'
                    }}
                  >
                    {isSubmitting ? '등록 중...' : '등록하기'}
                  </button>
                </div>
              </div>
              
              {/* 숨겨진 파일 입력 */}
              <HiddenFileInput
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileChange}
              />
            </FormContainer>
          </QuestionFormSection>
        )}
        
        {/* 상세 페이지가 아닐 때만 질문하기 버튼 표시 */}
        {!showDetail && (
          <BottomSection>
            <AskButton onClick={toggleQuestionForm}>
              질문하기
            </AskButton>
          </BottomSection>
        )}
      </ContentSection>
    </QnAContainer>
  );
};

/* 전체 브라우저 스크롤바 공간 예약으로 흔들림 방지 */
const QnAContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  position: relative;
  overflow: hidden;
  scrollbar-gutter: stable; /* 스크롤바 공간 항상 예약 */
`;

const SearchSection = styled.div`
  width: 100%;
  padding: 24px 16px 0 16px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 16px;
  flex-shrink: 0; /* 검색 영역은 고정 크기 */
`;

const SearchInputWrapper = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 12px 12px 44px; /* 왼쪽 패딩을 늘려서 아이콘 공간 확보 */
  background: var(--Fill-Normal, rgba(112, 115, 124, 0.08));
  overflow: hidden;
  border-radius: 12px;
  backdrop-filter: blur(32px);
  border: none;
  outline: none;
  
  color: var(--Label-Assistive, rgba(55, 56, 60, 0.28));
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.09px;
  
  &::placeholder {
    color: var(--Label-Assistive, rgba(55, 56, 60, 0.28));
  }
  
  &:focus {
    color: var(--Label-Normal, #171719);
  }
`;

const QuickAccessWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding-bottom: 8px;
`;

const QuickAccessText = styled.div`
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 20.01px;
  letter-spacing: 0.2px;
  word-wrap: break-word;
`;

const ChevronRightIcon = styled.div`
  width: 16px;
  height: 16px;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  transform: rotate(-90deg);
  
  svg {
    width: 16px;
    height: 16px;
    
    path {
      fill: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
    }
  }
`;

const ContentSection = styled.div<{ $showDetail?: boolean }>`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  position: relative;
  overflow: hidden;
  min-height: 0; /* flexbox가 제대로 작동하도록 */
`;

const QuestionsList = styled.div<{ $showDetail?: boolean }>`
  width: 100%;
  flex: 1;
  overflow-y: auto; /* 세로 스크롤 허용 */
  overflow-x: hidden; /* 가로 스크롤은 숨김 */
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  padding: 0 16px;
  padding-bottom: ${props => props.$showDetail ? '0' : '120px'}; /* 목록일 때만 하단 여백 */
  
  /* 스크롤바 숨기기 */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
`;

/* 질문 아이템 - 클릭 가능하도록 수정 */
const QuestionItem = styled.div`
  width: 100%;
  padding: 0 16px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: rgba(112, 115, 124, 0.04);
  }
`;

const QuestionContent = styled.div`
  width: 100%;
  padding: 16px 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 24px;
`;

const QuestionHeader = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 12px;
`;

const QuestionTitle = styled.div`
  width: 100%;
  color: var(--Label-Normal, #171719);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  word-wrap: break-word;
`;

const QuestionBody = styled.div`
  width: 100%;
  min-height: 44px;
  color: var(--Label-Neutral, rgba(46, 47, 51, 0.88));
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 21.99px;
  letter-spacing: 0.2px;
  word-wrap: break-word;
`;

const QuestionMeta = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LeftMeta = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const AuthorAvatar = styled.div`
  width: 24px;
  height: 24px;
  position: relative;
  border-radius: 50%;
  background: var(--Static-White, white);
  border: 1px solid var(--Line-Normal-Alternative, rgba(112, 115, 124, 0.08));
  background-image: url('/images/default-avatar.png');
  background-size: cover;
  background-position: center;
`;

const AuthorName = styled.div`
  color: var(--Label-Neutral, rgba(46, 47, 51, 0.88));
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20.01px;
  letter-spacing: 0.2px;
  word-wrap: break-word;
`;

const Separator = styled.div`
  width: 1px;
  height: 12px;
  background: var(--Line-Normal-Normal, rgba(112, 115, 124, 0.22));
`;

const QuestionDate = styled.div`
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20.01px;
  letter-spacing: 0.2px;
  word-wrap: break-word;
`;

const RightMeta = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
`;

const AnswerCount = styled.div`
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20.01px;
  letter-spacing: 0.2px;
  word-wrap: break-word;
`;

const ViewCount = styled.div`
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20.01px;
  letter-spacing: 0.2px;
  word-wrap: break-word;
`;

const QuestionDivider = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 10px;
  
  &::after {
    content: '';
    width: 100%;
    height: 1px;
    background: var(--Line-Normal-Normal, rgba(112, 115, 124, 0.22));
  }
`;

const BottomGradient = styled.div<{ $showDetail?: boolean }>`
  width: 100%;
  height: 60px;
  position: absolute;
  bottom: ${props => props.$showDetail ? '0' : '60px'}; /* 상세에서는 맨 아래, 목록에서는 버튼 바로 위 */
  left: 0;
  background: linear-gradient(
    0deg,
    var(--Background-Normal-Normal, white) 0%,
    rgba(255, 255, 255, 0.9) 20%,
    rgba(255, 255, 255, 0.7) 40%,
    rgba(255, 255, 255, 0.5) 60%,
    rgba(255, 255, 255, 0.3) 80%,
    transparent 100%
  );
  pointer-events: none;
  z-index: 10;
`;

const BottomSection = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 120px; /* 그라데이션 영역을 더 넓게 */
  padding: 0 16px 16px 16px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.3) 30%, rgba(255, 255, 255, 0.8) 70%, var(--Background-Normal-Normal, white) 100%);
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-start;
  z-index: 20;
  pointer-events: none; /* 배경은 클릭 불가 */
  
  button {
    pointer-events: auto; /* 버튼만 클릭 가능 */
  }
`;

const QuestionFormSection = styled.div<{ $isClosing?: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 80px; /* "질문하기" 버튼 공간(80px) 확보 */
  padding: 16px;
  background: var(--Background-Elevated-Normal, white);
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 16px;
  overflow: hidden;
  z-index: 20;
  
  /* 애니메이션 */
  animation: ${props => props.$isClosing ? 'slideDownToBottom' : 'slideUpFromBottom'} 0.3s ease-out forwards;
  
  @keyframes slideUpFromBottom {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
  
  @keyframes slideDownToBottom {
    from {
      transform: translateY(0);
    }
    to {
      transform: translateY(100%);
    }
  }
`;

const FormContainer = styled.div`
  width: 100%;
  flex: 1; /* 남은 공간을 모두 차지 */
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 16px;
  overflow: hidden; /* 넘치는 부분은 숨김 */
  padding-top: 16px;
`;

const TitleInputContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 8px;
`;

const TitleInput = styled.input`
  width: 100%;
  height: 48px;
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.22);
  outline: none;
  transition: all 0.2s ease;
  
  color: #171719;
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.09px;
  
  &::placeholder {
    color: rgba(55, 56, 60, 0.28);
  }
  
  &:hover {
    border-color: rgba(112, 115, 124, 0.4);
  }

  &:focus {
    border-color: #448181;
    box-shadow: 0 0 0 3px rgba(68, 129, 129, 0.1);
  }
`;



const ImageButton = styled.button<{ disabled?: boolean }>`
  width: 24px;
  height: 24px;
  background: none;
  border: none;
  cursor: ${(props) => props.disabled ? 'not-allowed' : 'pointer'};
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    color: var(--Label-Normal, #171719);
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const CodeButton = styled.button`
  width: 24px;
  height: 24px;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    color: var(--Label-Normal, #171719);
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;



const AttachedImages = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`;

const AttachedImage = styled.img`
  width: 100%;
  height: 400px;
  object-fit: cover;
  display: block;
`;

const QuestionContentDisplay = styled.div`
  white-space: pre-wrap;
  line-height: 1.6;
  color: var(--Label-Neutral, rgba(46, 47, 51, 0.88));
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  letter-spacing: 0.2px;
  word-wrap: break-word;
  
  /* 본문 미리보기 2줄로 제한 */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;

  /* global.css의 font: inherit 덮어쓰기 */
  b, strong {
    font-weight: 700 !important;
    font-family: 'Pretendard JP', sans-serif !important;
  }

  u {
    text-decoration: underline !important;
  }

  strike, s, del {
    text-decoration: line-through !important;
  }

  i, em {
    font-style: italic !important;
    font-family: 'Pretendard JP', sans-serif !important;
  }

  img {
    max-width: 50%;
    height: auto;
    border-radius: 8px;
    margin: 10px 0;
  }

  pre {
    background: var(--Fill-Weak, rgba(112, 115, 124, 0.04));
    border: 1px solid var(--Line-Normal-Alternative, rgba(112, 115, 124, 0.08));
    border-radius: 8px;
    padding: 16px;
    margin: 16px 0;
    overflow-x: auto;
    
    code {
      background: none !important;
      border: none !important;
      padding: 0 !important;
      color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
      font-family: 'Courier New', monospace !important;
      font-size: 14px;
      line-height: 20px;
      white-space: pre-wrap;
      word-break: break-word;
    }
  }
  
  code {
    background: var(--Fill-Weak, rgba(112, 115, 124, 0.04));
    border: 1px solid var(--Line-Normal-Alternative, rgba(112, 115, 124, 0.08));
    border-radius: 4px;
    padding: 2px 6px;
    font-family: 'Courier New', monospace !important;
    font-size: 14px;
    color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  }
`;



const AskButton = styled.button`
  width: 100%;
  padding: 12px 28px;
  position: relative;
  background: var(--Primary-Normal, #448181);
  overflow: hidden;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  
  color: var(--Static-White, white);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  word-wrap: break-word;
  
  /* 모든 클릭 효과 제거 */
`;

/* 상세 페이지 스타일 컴포넌트들 */
const QuestionDetailContainer = styled.div`
  width: 100%;
  height: 100%;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow-y: auto;
  
  /* 스크롤바 숨기기 */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const BackButton = styled.button`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 20.01px;
  letter-spacing: 0.20px;
  padding: 0;
  padding-bottom: 8px;
`;

const BackIcon = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BackText = styled.span`
  color: inherit;
`;

const QuestionDetailContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const QuestionDetailHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const QuestionDetailTitle = styled.h1`
  color: var(--Label-Normal, #171719);
  font-size: 22px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 30.01px;
  word-wrap: break-word;
  margin: 0;
`;

const QuestionDetailMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DetailDivider = styled.div`
  width: 100%;
  height: 1px;
  background: var(--Line-Normal-Normal, rgba(112, 115, 124, 0.22));
`;

const QuestionDetailBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const QuestionDetailText = styled.div`
  color: var(--Label-Normal, #171719);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 26px;
  letter-spacing: 0.09px;
  word-wrap: break-word;

  /* global.css의 font: inherit 덮어쓰기 */
  b, strong {
    font-weight: 700 !important;
    font-family: 'Pretendard JP', sans-serif !important;
  }

  u {
    text-decoration: underline !important;
  }

  strike, s, del {
    text-decoration: line-through !important;
  }

  i, em {
    font-style: italic !important;
    font-family: 'Pretendard JP', sans-serif !important;
  }

  img {
    max-width: 50%;
    height: auto;
    border-radius: 8px;
    margin: 10px 0;
  }

  pre {
    background: var(--Fill-Weak, rgba(112, 115, 124, 0.04));
    border: 1px solid var(--Line-Normal-Alternative, rgba(112, 115, 124, 0.08));
    border-radius: 8px;
    padding: 16px;
    margin: 16px 0;
    overflow-x: auto;
    
    code {
      background: none !important;
      border: none !important;
      padding: 0 !important;
      color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
      font-family: 'Courier New', monospace !important;
      font-size: 14px;
      line-height: 20px;
      white-space: pre-wrap;
      word-break: break-word;
    }
  }
  
  code {
    background: var(--Fill-Weak, rgba(112, 115, 124, 0.04));
    border: 1px solid var(--Line-Normal-Alternative, rgba(112, 115, 124, 0.08));
    border-radius: 4px;
    padding: 2px 6px;
    font-family: 'Courier New', monospace !important;
    font-size: 14px;
    color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  }
`;



const AnswerSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const AnswerHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
`;

const AnswerIcon = styled.div`
  width: 24px;
  height: 24px;
  color: var(--Label-Normal, #171719);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AnswerTitle = styled.h2`
  color: var(--Label-Normal, #171719);
  font-size: 20px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 28px;
  word-wrap: break-word;
  margin: 0;
`;

const AnswerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const AnswerItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const AnswerAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const AnswerAuthorInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const AnswerAuthorName = styled.div`
  color: var(--Label-Normal, #171719);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  word-wrap: break-word;
`;

const AnswerDate = styled.div`
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 12px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 16.01px;
  letter-spacing: 0.30px;
  word-wrap: break-word;
`;

const AnswerContent = styled.div`
  color: var(--Label-Normal, #171719);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 26px;
  letter-spacing: 0.09px;
  word-wrap: break-word;

  /* global.css의 font: inherit 덮어쓰기 */
  b, strong {
    font-weight: 700 !important;
    font-family: 'Pretendard JP', sans-serif !important;
  }

  u {
    text-decoration: underline !important;
  }

  strike, s, del {
    text-decoration: line-through !important;
  }

  i, em {
    font-style: italic !important;
    font-family: 'Pretendard JP', sans-serif !important;
  }

  img {
    max-width: 50%;
    height: auto;
    border-radius: 8px;
    margin: 10px 0;
  }

  pre {
    background: var(--Fill-Weak, rgba(112, 115, 124, 0.04));
    border: 1px solid var(--Line-Normal-Alternative, rgba(112, 115, 124, 0.08));
    border-radius: 8px;
    padding: 16px;
    margin: 16px 0;
    overflow-x: auto;
    
    code {
      background: none !important;
      border: none !important;
      padding: 0 !important;
      color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
      font-family: 'Courier New', monospace !important;
      font-size: 14px;
      line-height: 20px;
      white-space: pre-wrap;
      word-break: break-word;
    }
  }
  
  code {
    background: var(--Fill-Weak, rgba(112, 115, 124, 0.04));
    border: 1px solid var(--Line-Normal-Alternative, rgba(112, 115, 124, 0.08));
    border-radius: 4px;
    padding: 2px 6px;
    font-family: 'Courier New', monospace !important;
    font-size: 14px;
    color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  }
`;

/* 로딩 및 에러 상태 스타일 */
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 20px;
  width: 100%;
`;

const LoadingText = styled.div`
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 60px 20px;
  width: 100%;
  gap: 16px;
`;

const ErrorText = styled.div`
  color: #FF4444;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  text-align: center;
`;

const RetryButton = styled.button`
  padding: 8px 16px;
  background: var(--Primary-Strong, #296768);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--Primary-Heavy, #1e4f50);
  }
  
  &:active {
    transform: scale(0.98);
  }
`;

/* 빈 Q&A 상태 스타일 */
const EmptyQnAContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 80px 20px;
  width: 100%;
`;

const EmptyQnAText = styled.div`
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 18px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 26px;
  text-align: center;
  margin-bottom: 8px;
`;

const EmptyQnASubText = styled.div`
  color: var(--Label-Assistive, rgba(55, 56, 60, 0.28));
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
`;

/* 상세 페이지 로딩/에러 상태 스타일 */
const DetailLoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 20px;
  width: 100%;
`;

const DetailLoadingText = styled.div`
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
`;

const DetailErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 60px 20px;
  width: 100%;
  gap: 16px;
`;

const DetailErrorText = styled.div`
  color: #FF4444;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  text-align: center;
`;

const DetailRetryButton = styled.button`
  padding: 8px 16px;
  background: var(--Primary-Strong, #296768);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--Primary-Heavy, #1e4f50);
  }
  
  &:active {
    transform: scale(0.98);
  }
`;

/* 챕터 태그 스타일 */
const ChapterTag = styled.span`
  padding: 2px 8px;
  background: var(--Primary-Lighter, #E6F2F2);
  color: var(--Primary-Strong, #296768);
  border-radius: 4px;
  font-size: 12px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 16px;
`;

/* 답변 이미지 스타일 */
const ReplyImages = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 12px;
`;

const ReplyImage = styled.img`
  max-width: 200px;
  max-height: 200px;
  border-radius: 8px;
  object-fit: cover;
  cursor: pointer;
  transition: transform 0.2s ease;
  
  &:hover {
    transform: scale(1.02);
  }
`;

/* 답변 없음 상태 스타일 */
const NoAnswerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 20px;
  width: 100%;
`;

const NoAnswerText = styled.div`
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
`;

/* 로딩 스피너 스타일 */
const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(55, 56, 60, 0.1);
  border-top: 2px solid rgba(55, 56, 60, 0.28);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export default StreamingQnA;
